import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGE_LABEL, fmtMoney, RISK, ROLE_HOME_DEFAULTS } from "@/lib/constants";
import { ScoreBadge, RiskDot } from "@/components/RiskDot";
import { cn } from "@/lib/utils";
import { LockSimple } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { TrendUp, Buildings, Users, CurrencyEur, Truck } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MyTasksWidget from "@/components/MyTasksWidget";
import SmartInbox from "@/components/SmartInbox";
import BlingUpdateBanner from "@/components/BlingUpdateBanner";

const DEPT_DEFS = {
  design: { label: "Design", tiles: [
    { label: "Clarificări tehnice", key: "clarificari_g2", accent: "text-blue-400" },
    { label: "Aprobări client", key: "aprobari_g3", accent: "text-violet-400" },
    { label: "Revizii active", key: "revizii_active", accent: "text-amber-400" },
    { label: "Revizii ≥3", key: "revizii_r02", accent: "text-red-400", danger: true },
  ]},
  achizitii: { label: "Achiziții", tiles: [
    { label: "Comenzi emise", key: "comenzi_emise", accent: "text-blue-400" },
    { label: "Comenzi confirmate", key: "comenzi_confirmate", accent: "text-emerald-400" },
    { label: "Recepții în așteptare", key: "receptii_asteptare", accent: "text-amber-400" },
    { label: "Materiale critice", key: "materiale_critice", accent: "text-red-400", danger: true },
  ]},
  productie: { label: "Producție", tiles: [
    { label: "Proiecte în producție", key: "proiecte_g5", accent: "text-violet-400" },
    { label: "Recepții CTC în lucru", key: "receptii_in_lucru", accent: "text-amber-400" },
    { label: "Ready to Ship", key: "ready_to_ship", accent: "text-emerald-400" },
    { label: "CTC de finalizat", key: "ctc_de_finalizat", accent: "text-blue-400" },
  ]},
  montaj: { label: "Montaj", tiles: [
    { label: "De mobilizat", key: "proiecte_g6", accent: "text-blue-400" },
    { label: "În montaj", key: "proiecte_g7", accent: "text-violet-400" },
    { label: "Rapoarte teren", key: "rapoarte_teren", accent: "text-emerald-400" },
    { label: "Blocaje", key: "blocaje", accent: "text-red-400", danger: true },
  ]},
  logistica: { label: "Logistică", tiles: [
    { label: "De mobilizat", key: "de_mobilizat_g6", accent: "text-blue-400" },
    { label: "În teren", key: "in_teren_g7", accent: "text-violet-400" },
    { label: "Transporturi/vehicule", key: "transporturi", accent: "text-amber-400" },
    { label: "Rapoarte teren", key: "rapoarte_teren", accent: "text-emerald-400" },
  ]},
  financiar: { label: "Financiar", tiles: [
    { label: "Avansuri neîncasate", key: "avansuri_neincasate", accent: "text-red-400", danger: true },
    { label: "Buget total", key: "buget_total", accent: "text-blue-400", money: true },
    { label: "Costuri total", key: "costuri_total", accent: "text-amber-400", money: true },
    { label: "Marjă %", key: "marja_pct", accent: "text-emerald-400", pct: true },
  ]},
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon size={20} weight="bold" className={accent} />
      </div>
      <div className="mt-3 font-head text-3xl font-extrabold tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

function DeptBoard({ data, which }) {
  const def = DEPT_DEFS[which];
  const d = data?.[which];
  if (!def || !d) return null;
  const fmt = (t) => t.money ? fmtMoney(d[t.key]) : t.pct ? `${d[t.key]}%` : d[t.key];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid={`dept-${which}`}>
      {def.tiles.map((t) => (
        <div key={t.key} className="rounded-lg border border-border bg-white/[0.02] p-4">
          <div className="text-xs font-semibold text-muted-foreground">{t.label}</div>
          <div className={cn("mt-1 font-head text-2xl font-extrabold tabular-nums", t.danger && d[t.key] ? "text-red-400" : t.accent)}>{fmt(t)}</div>
        </div>
      ))}
    </div>
  );
}

function ReadyToShipWidget({ items }) {
  if (!items) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="ready-to-ship-widget">
      <div className="mb-4 flex items-center gap-2">
        <Truck size={20} weight="bold" className="text-emerald-400" />
        <h3 className="font-head text-lg font-bold">Ready-to-Ship în 7 zile</h3>
      </div>
      <div className="space-y-2">
        {items.map((p) => {
          const late = p.days_left < 0;
          return (
            <Link key={p.id} to={`/projects/${p.id}`} className="flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-white/5" data-testid={`rts-${p.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{p.name}</div>
                <div className="truncate font-mono text-xs text-muted-foreground">{p.code}</div>
              </div>
              <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-xs font-bold tabular-nums",
                late ? "bg-red-500/15 text-red-400" : p.days_left <= 1 ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400")}>
                {late ? "întârziat" : `${Math.max(0, Math.ceil(p.days_left))} zile`}
              </span>
            </Link>
          );
        })}
        {items.length === 0 && <p className="text-sm text-muted-foreground">Niciun produs Ready-to-Ship în următoarele 7 zile.</p>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [dept, setDept] = useState(null);
  const [rts, setRts] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/departments").then((r) => setDept(r.data)).catch(() => {});
    api.get("/dashboard/ready-to-ship").then((r) => setRts(r.data)).catch(() => {});
  }, [isAdmin]);

  // ---- Non-admin: acces la dashboard principal, dar valorile sunt mascate (doar adminii le văd) ----
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 fade-up" data-testid="dashboard">
        <BlingUpdateBanner />
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Bun venit</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tighter">Salut, {user?.name?.split(" ")[0]} 👋</h1>
        </div>
        <div className="mb-6"><MyTasksWidget /></div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Users} label="Total Lead-uri" value="•••" accent="text-blue-400" />
          <Stat icon={Buildings} label="Proiecte Active" value="•••" accent="text-violet-400" />
          <Stat icon={CurrencyEur} label="Valoare Pipeline" value="•••" accent="text-amber-400" />
          <Stat icon={TrendUp} label="Valoare Câștigată" value="•••" accent="text-emerald-400" />
        </div>
        <div className="mt-6 flex items-center gap-3 rounded-xl border border-border bg-white/[0.02] p-5 text-sm text-muted-foreground" data-testid="values-locked-note">
          <LockSimple size={20} weight="bold" className="shrink-0 text-amber-400" />
          Valorile detaliate (pipeline, riscuri, financiar, lead-uri) sunt vizibile doar administratorilor.
        </div>
      </div>
    );
  }

  const roleDefault = ROLE_HOME_DEFAULTS[user?.role];
  const homeDept = Object.keys(DEPT_DEFS).includes(user?.home)
    ? user.home
    : (user?.home === "general" ? null : (Object.keys(DEPT_DEFS).includes(roleDefault) ? roleDefault : null));

  // ---- Home per rol: panou departament ca ecran principal ----
  if (homeDept) {
    const def = DEPT_DEFS[homeDept];
    return (
      <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="role-home">
        <BlingUpdateBanner />
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Panoul tău · {def.label}</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tighter">Salut, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="mt-1 text-sm text-muted-foreground">Ce ai de urmărit azi în departamentul {def.label}.</p>
        </div>
        <div className="mb-6"><MyTasksWidget /></div>
        <div className="mb-6 rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 font-head text-lg font-bold">Indicatori {def.label}</h3>
          {dept ? <DeptBoard data={dept} which={homeDept} /> : <p className="text-sm text-muted-foreground">Se încarcă…</p>}
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <ReadyToShipWidget items={rts} />
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="mb-3 font-head text-lg font-bold">Acces rapid</h3>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => navigate("/projects")} className="rounded-lg border border-border px-3 py-2.5 text-left text-sm hover:bg-white/5" data-testid="quick-projects">Proiecte</button>
              <button onClick={() => navigate("/inventory")} className="rounded-lg border border-border px-3 py-2.5 text-left text-sm hover:bg-white/5" data-testid="quick-inventory">Gestiune</button>
            </div>
          </div>
        </div>
        <SmartInbox department={homeDept} showAssistant={false} />
      </div>
    );
  }

  // ---- Dashboard general ----
  if (!stats) return <div className="p-8 text-muted-foreground">Se încarcă dashboard…</div>;

  const chartData = Object.entries(stats.stage_counts).map(([k, v]) => ({ name: STAGE_LABEL[k] || k, value: v, key: k }));
  const barColor = { nou: "#64748b", calificat: "#3B82F6", oferta: "#8B5CF6", negociere: "#F59E0B", castigat: "#10B981", pierdut: "#EF4444" };

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 fade-up" data-testid="dashboard">
      <BlingUpdateBanner />
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Bun venit</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tighter">Salut, {user?.name?.split(" ")[0]} 👋</h1>
      </div>

      <div className="mb-6"><MyTasksWidget /></div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label="Total Lead-uri" value={stats.total_leads} accent="text-blue-400" />
        <Stat icon={Buildings} label="Proiecte Active" value={stats.active_projects} accent="text-violet-400" />
        <Stat icon={CurrencyEur} label="Valoare Pipeline" value={fmtMoney(stats.pipeline_value)} accent="text-amber-400" />
        <Stat icon={TrendUp} label="Valoare Câștigată" value={fmtMoney(stats.won_value)} accent="text-emerald-400" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2">
          <h3 className="mb-4 font-head text-lg font-bold">Pipeline pe etape</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {chartData.map((d) => <Cell key={d.key} fill={barColor[d.key] || "#3B82F6"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 font-head text-lg font-bold">Risc proiecte</h3>
          <div className="space-y-4">
            {["verde", "galben", "rosu"].map((lvl) => (
              <div key={lvl} className="flex items-center justify-between">
                <RiskDot level={lvl} label size="lg" />
                <span className="font-head text-2xl font-bold tabular-nums">{stats.risk_counts[lvl] || 0}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 border-t border-border pt-4">
            <div className="text-xs text-muted-foreground">Total proiecte</div>
            <div className="font-head text-xl font-bold">{stats.total_projects}</div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 font-head text-lg font-bold">Lead-uri „fierbinți” (Scor AI)</h3>
          <div className="space-y-2">
            {stats.hot_leads.map((l) => (
              <Link key={l.id} to={`/leads/${l.id}`} className="flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-white/5" data-testid={`hot-lead-${l.id}`}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{l.company}</div>
                  <div className="truncate text-xs text-muted-foreground">{l.product_type || "—"}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">{fmtMoney(l.estimated_value)}</span>
                  <ScoreBadge score={l.ai_score} color={l.score_color} />
                </div>
              </Link>
            ))}
          </div>
        </div>

        <ReadyToShipWidget items={rts} />
      </div>

      {dept && (
        <div className="mt-6 rounded-xl border border-border bg-card p-6" data-testid="department-boards">
          <h3 className="mb-1 font-head text-lg font-bold">Panouri pe departament</h3>
          <p className="mb-4 text-xs text-muted-foreground">Fiecare departament vede exact ce cere fluxul operațional (secțiunea 11).</p>
          <Tabs defaultValue="design">
            <TabsList data-testid="dept-tabs">
              <TabsTrigger value="design" data-testid="dept-tab-design">Design</TabsTrigger>
              <TabsTrigger value="achizitii" data-testid="dept-tab-achizitii">Achiziții</TabsTrigger>
              <TabsTrigger value="productie" data-testid="dept-tab-productie">Producție</TabsTrigger>
              <TabsTrigger value="montaj" data-testid="dept-tab-montaj">Montaj</TabsTrigger>
              <TabsTrigger value="logistica" data-testid="dept-tab-logistica">Logistică</TabsTrigger>
              <TabsTrigger value="financiar" data-testid="dept-tab-financiar">Financiar</TabsTrigger>
            </TabsList>
            {Object.keys(DEPT_DEFS).map((k) => (
              <TabsContent key={k} value={k} className="mt-4"><DeptBoard data={dept} which={k} /></TabsContent>
            ))}
          </Tabs>
        </div>
      )}
      {user?.role === "admin" && <SmartInbox />}
    </div>
  );
}
